import { normalizeTitle } from './title-model';

/** Pure teaching models. These transitions do not execute Python or contact a server. */
export interface ModelTask {
  id: number;
  title: string;
  completed: boolean;
}
export interface ModelOutput {
  label: string;
  value: string;
}
export interface ModelFrame {
  outputs: ModelOutput[];
  explanation: string;
  warning?: boolean;
}
const output = (label: string, value: string): ModelOutput => ({ label, value });
const json = (value: unknown) => JSON.stringify(value, null, 2);
export const sampleTasks: readonly ModelTask[] = [
  { id: 1, title: 'Read notes', completed: false },
  { id: 7, title: 'Call Alex', completed: true },
  { id: 12, title: 'Book room', completed: false },
];

export const responsibilitySteps = [
  'Accept bytes',
  'Match route',
  'Validate input',
  'Apply and save',
  'Encode response',
] as const;
export function responsibilityFrame(framework: boolean, step: number): ModelFrame {
  const index = Math.max(0, Math.min(4, step - 1));
  const owners = framework
    ? [
        'Uvicorn',
        'FastAPI',
        'Pydantic + your declared rules',
        'Your task and storage code',
        'FastAPI + Uvicorn',
      ]
    : [
        'Your socket listener',
        'Your method/path branches',
        'Your JSON and input checks',
        'Your task and storage code',
        'Your wire helper',
      ];
  return {
    outputs: [
      output(
        'Current responsibility',
        step ? (responsibilitySteps[index] ?? '') : 'Waiting for a request',
      ),
      output('Who does this work?', step ? (owners[index] ?? '') : 'No work has run'),
      output(
        'Application promise',
        'POST /tasks must create one valid task and save it before returning 201.',
      ),
    ],
    explanation:
      step === 4
        ? 'The framework does not decide whether the new task is saved. Your application still owns this promise.'
        : step
          ? 'The responsibility remains. Switching the design changes who implements it, not whether the work is needed.'
          : 'Predict which responsibilities can move into a framework. Then follow the same create request.',
  };
}

export const registrationSteps = [
  'Import the module',
  'Call create_app()',
  'Register GET /health',
  'Start serving the app',
  'Receive GET /health',
  'Receive GET /health again',
] as const;
export function registrationFrame(directCall: boolean, step: number): ModelFrame {
  const calls = directCall ? (step >= 3 ? 1 : 0) : Math.max(0, step - 4);
  return {
    outputs: [
      output(
        'Startup code',
        directCall
          ? '# inside create_app\nhealth()\n# calls the function now'
          : '# inside create_app\n@application.get("/health")\ndef health(): ...\n# registers a handler',
      ),
      output(
        'Application lifecycle',
        step < 1
          ? 'Module not imported'
          : step < 2
            ? 'Module import begins; factory is defined'
            : step < 3
              ? 'Factory creates application object'
              : step < 4
                ? 'Setup complete; no listener yet'
                : 'Uvicorn is serving this application',
      ),
      output(
        'Route table',
        step >= 3 && !directCall ? 'GET /health → health' : '(no registered route)',
      ),
      output('Handler calls', String(calls)),
      output(
        'Latest HTTP response',
        step < 5 ? '(no request yet)' : directCall ? '404 Not Found' : '200 {"status":"ok"}',
      ),
    ],
    explanation: !step
      ? 'Importing the module runs its top-level setup. Does setup call the handler or register it?'
      : step < 3
        ? 'Defining and calling the factory are separate steps. It builds an application; it does not itself open a listening socket.'
        : directCall
          ? 'Calling a function once during startup does not register an HTTP route. Later requests have no matching handler.'
          : step <= 4
            ? 'Registration stores the function to call later. Even after Uvicorn starts serving, handler calls remain zero until a matching request arrives.'
            : 'A matching request makes the framework call the registered handler. Registration and execution happen at different times.',
    warning: directCall && step >= 5,
  };
}

export const inputExamples = {
  path: {
    request: 'GET /tasks/7',
    declaration: 'def get_task(task_id: int):',
    source: 'Path segment: "7"',
    bound: 'task_id = 7 (integer)',
  },
  query: {
    request: 'GET /tasks?completed=false',
    declaration: 'def get_tasks(completed: bool | None = None):',
    source: 'Query text: "false"',
    bound: 'completed = False (boolean)',
  },
  omitted: {
    request: 'GET /tasks',
    declaration: 'def get_tasks(completed: bool | None = None):',
    source: 'No completed query parameter',
    bound: 'completed = None (no filter)',
  },
  body: {
    request: 'POST /tasks\n{"title":"Read notes"}',
    declaration: 'def post_task(body: CreateTask):',
    source: 'JSON body: title field',
    bound: 'body.title = "Read notes"',
  },
} as const;
export type InputExample = keyof typeof inputExamples;
export function inputFrame(example: InputExample, step: number): ModelFrame {
  const entry = inputExamples[example];
  return {
    outputs: [
      output('Incoming request', entry.request),
      output('Handler declaration', entry.declaration),
      output('Source of the value', step ? entry.source : '(not inspected)'),
      output('Bound argument', step > 1 ? entry.bound : '(not bound)'),
    ],
    explanation:
      step > 1
        ? 'The declaration tells the framework where this value comes from and how to interpret it. Request text and handler values are different representations.'
        : 'First locate the input, then interpret it using the route declaration. A missing optional filter means no filter; it is not False.',
  };
}

export const validationBodies = {
  valid: {
    route: 'POST /tasks',
    body: '{"title":"  Read notes  "}',
    valid: true,
    result: 'title = "Read notes"',
  },
  blank: {
    route: 'POST /tasks',
    body: '{"title":"   "}',
    valid: false,
    result: 'Title is empty after trimming.',
  },
  number: {
    route: 'POST /tasks',
    body: '{"title":42}',
    valid: false,
    result: 'StrictStr requires a string; a number is not a title.',
  },
  missing: { route: 'POST /tasks', body: '{}', valid: false, result: 'Required title is missing.' },
  extra: {
    route: 'POST /tasks',
    body: '{"title":"Read notes","id":99}',
    valid: false,
    result: 'The model forbids an undeclared id field.',
  },
  string: {
    route: 'PATCH /tasks/7',
    body: '{"completed":"false"}',
    valid: false,
    result: 'StrictBool requires a JSON boolean, not a string.',
  },
  boolean: {
    route: 'PATCH /tasks/7',
    body: '{"completed":false}',
    valid: true,
    result: 'completed = False',
  },
  array: {
    route: 'POST /tasks',
    body: '[]',
    valid: false,
    result: 'Valid JSON, but the request model requires an object.',
  },
} as const;
export type ValidationBody = keyof typeof validationBodies;
export function apiValidationFrame(example: ValidationBody, step: number): ModelFrame {
  const entry = validationBodies[example];
  return {
    outputs: [
      output('Request body', `${entry.route}\n${entry.body}`),
      output('Model decision', step ? entry.result : '(validation has not run)'),
      output(
        'Handler boundary',
        step > 1 && entry.valid
          ? 'Handler may now run with validated values.'
          : step && !entry.valid
            ? '422 response; handler never runs.'
            : 'Handler has not run.',
      ),
      output(
        'Current data',
        'Unchanged in this model: validation alone does not save or change tasks.',
      ),
    ],
    explanation: !step
      ? 'All these bodies are syntactically valid JSON. Predict which satisfy the application’s declared input contract.'
      : entry.valid
        ? 'Validation produces usable values. The handler still has to perform and save the requested change.'
        : 'Reject before the handler. A request model protects this HTTP entry point; task functions must still protect direct callers such as the CLI.',
    warning: step > 0 && !entry.valid,
  };
}

export type ResourceOperation =
  'get' | 'missing' | 'set' | 'toggle' | 'delete' | 'filter' | 'invalid-id' | 'unsupported';
export function resourceFrame(operation: ResourceOperation, count: number): ModelFrame {
  const task = { id: 7, title: 'Call Alex', completed: false };
  if (operation === 'set') task.completed = count > 0;
  if (operation === 'toggle') task.completed = count % 2 === 1;
  const removed = operation === 'delete' && count > 0;
  const requests: Record<ResourceOperation, string> = {
    get: 'GET /tasks/7',
    missing: 'GET /tasks/99',
    set: 'PATCH /tasks/7\n{"completed":true}',
    toggle: 'Imaginary command: toggle task 7',
    delete: 'DELETE /tasks/7',
    filter: 'GET /tasks?completed=true',
    'invalid-id': 'GET /tasks/seven',
    unsupported: 'PUT /tasks/7',
  };
  const request = requests[operation];
  const response = !count
    ? '(not requested)'
    : operation === 'filter'
      ? '200 []\n(no completed tasks in this view)'
      : operation === 'invalid-id'
        ? '422 · task_id must be an integer'
        : operation === 'unsupported'
          ? '405 Method Not Allowed\nInspect the actual Allow header for supported methods.'
          : operation === 'missing' || (operation === 'delete' && count > 1)
            ? '404 Task not found'
            : operation === 'delete'
              ? '204 No Content\n(no response body)'
              : operation === 'toggle'
                ? 'Task completion reversed'
                : `200 ${json(task)}`;
  return {
    outputs: [
      output('Operation', request),
      output('Accepted records', json(removed ? [] : [task])),
      output(
        'Next-ID counter',
        'next_id = 8\nReads, completion changes and deletion do not allocate or reuse an ID.',
      ),
      output('Latest outcome', response),
    ],
    explanation: !count
      ? 'The path names a stable task ID. Predict what repeating this same operation should do.'
      : operation === 'filter'
        ? 'The completed view is empty, but the open task still exists in current data. Filtering chooses which tasks to show; it does not delete them.'
        : operation === 'invalid-id'
          ? 'The path contains a value that is not a valid integer. The server does not look up or change a task.'
          : operation === 'unsupported'
            ? 'This application has no PUT handler for the task path. Method support and whether a task exists are different decisions.'
            : operation === 'set'
              ? 'Setting completed to true twice leaves it true. The request carries the desired value.'
              : operation === 'toggle'
                ? 'Toggling twice reverses the first change. “Repeat” has a different meaning from setting a desired value.'
                : removed && count > 1
                  ? 'The record remains deleted, but this API reports 404 for the second deletion. Repeated final state does not require identical response statuses.'
                  : operation === 'missing'
                    ? 'The ID is a valid integer, but no record has that identity. This is absence, not malformed input.'
                    : 'The HTTP contract describes both the resulting state and the response. An empty successful deletion has no JSON body.',
  };
}

export type AcceptanceOutcome = 'saved' | 'failed' | 'lost';
export const acceptanceSteps = [
  'Prepare working copy',
  'Try saving',
  'Use saved copy',
  'Deliver response',
] as const;
export function acceptanceFrame(outcome: AcceptanceOutcome, step: number): ModelFrame {
  const saved = step >= 2 && outcome !== 'failed';
  const accepted = saved && step >= 3;
  return {
    outputs: [
      output('Working copy', step ? 'id: 4 · Read notes\nnext_id: 5' : '(not prepared)'),
      output('Saved snapshot', saved ? 'Task 4 saved; next_id = 5' : 'No new task; next_id = 4'),
      output(
        'Current data in memory',
        accepted ? 'Task 4 accepted; next_id = 5' : 'No new task; next_id = 4',
      ),
      output(
        'Client’s evidence',
        step < 4
          ? '(waiting)'
          : outcome === 'failed'
            ? '503 · save rejected; current data unchanged'
            : outcome === 'lost'
              ? 'No response received · outcome uncertain'
              : '201 · accepted task 4',
      ),
    ],
    explanation:
      step < 2
        ? 'The working copy is separate from current data in memory. Predict what remains if saving this copy fails.'
        : outcome === 'failed'
          ? 'Saving failed, so the server keeps its current data. It must not report that a task was created.'
          : step < 4
            ? 'After saving, the working copy can replace current data in memory. The client has not yet received confirmation.'
            : outcome === 'lost'
              ? 'The task is already saved. Losing the reply does not undo it. A client must inspect state before blindly repeating a create.'
              : 'The server saved the copy and updated its current data before replying. The client now has confirmation.',
    warning: (step >= 2 && outcome === 'failed') || (step >= 4 && outcome === 'lost'),
  };
}

export type LockScope = 'none' | 'write' | 'whole';
export const unlockedSteps = [
  'A copies state',
  'B copies state',
  'A saves and updates memory',
  'B saves and updates memory',
] as const;
export const lockedSteps = [
  'A acquires lock and copies',
  'B waits for A’s lock',
  'A saves, updates memory and releases',
  'B locks, copies, saves and updates memory',
] as const;
export function lockingFrame(scope: LockScope, step: number): ModelFrame {
  const whole = scope === 'whole';
  const tasks =
    step < 3
      ? []
      : step === 3
        ? [{ id: 4, title: 'A: Read notes' }]
        : whole
          ? [
              { id: 4, title: 'A: Read notes' },
              { id: 5, title: 'B: Call Alex' },
            ]
          : [{ id: 4, title: 'B: Call Alex' }];
  return {
    outputs: [
      output(
        'A’s working copy',
        step ? 'Based on next_id = 4\nnew task: #4 Read notes' : '(not copied)',
      ),
      output(
        'B’s working copy',
        whole
          ? step < 4
            ? '(waiting; no stale copy)'
            : 'Based on next_id = 5\nnew task: #5 Call Alex'
          : step < 2
            ? '(not copied)'
            : 'Based on next_id = 4\nnew task: #4 Call Alex',
      ),
      output(
        'Current data and saved file',
        json({ tasks, next_id: step < 3 ? 4 : step === 4 && whole ? 6 : 5 }),
      ),
      output(
        'Lock boundary',
        scope === 'whole'
          ? 'copy → change → save → update memory\nOne lock across the whole sequence'
          : scope === 'write'
            ? 'Only each write is locked. Both working copies were already based on old state.'
            : 'No shared lock',
      ),
    ],
    explanation:
      step < 4
        ? whole
          ? 'B cannot copy current data until A’s complete change finishes. The lock protects the decision as well as the write.'
          : 'Both handlers can copy next_id = 4 before either accepts a change. Locking only writes cannot repair an already stale working copy.'
        : whole
          ? 'Both tasks survive with different IDs. This lock coordinates threads in one process, not separate Uvicorn workers or a running CLI.'
          : 'Lost update: B’s complete snapshot replaced A’s task. A write-only lock orders file writes but still loses a valid change.',
    warning: step === 4 && !whole,
  };
}

export function valueChange(binding: 'const' | 'let', operation: 'mutate' | 'replace') {
  const allowed = binding === 'let' || operation === 'mutate';
  return {
    allowed,
    value: allowed ? { title: 'Read HTTP' } : { title: 'Read notes' },
    statement:
      operation === 'mutate' ? 'task.title = "Read HTTP"' : 'task = { title: "Read HTTP" }',
    explanation: allowed
      ? operation === 'mutate'
        ? 'The binding still points to the same object. const does not freeze that object’s properties.'
        : 'let allows this name to point to a replacement object.'
      : 'const forbids reassigning the binding. This says nothing about whether the original object’s properties can change.',
  };
}

export function layoutFrame(width: number, padding: number, wrap: boolean) {
  const available = Math.max(0, width - padding * 2);
  const required = 260;
  return {
    available,
    required,
    stacked: wrap && available < required,
    overflow: !wrap && available < required,
    explanation:
      available >= required
        ? 'The input and button fit on one row. Padding reduces the space available inside the container.'
        : wrap
          ? 'The second item wraps onto a new row. The controls remain usable at this narrower width.'
          : 'The items need more room than this container has. Without wrapping they overflow.',
  };
}

export function originFrame(target: 'relative' | 'loopback' | 'file'): ModelFrame {
  const origin = 'https://workspace-preview.example';
  return {
    outputs: [
      output('Page location', target === 'file' ? 'file:///project/index.html' : `${origin}/`),
      output(
        'Fetch expression',
        target === 'loopback' ? 'fetch("http://127.0.0.1:8000/tasks")' : 'fetch("/tasks")',
      ),
      output(
        'Resolved destination',
        target === 'relative'
          ? `${origin}/tasks\n→ preview gateway → workspace API`
          : target === 'loopback'
            ? 'http://127.0.0.1:8000/tasks\n→ browser’s own machine'
            : 'file:///tasks\n→ no HTTP application origin',
      ),
    ],
    explanation:
      target === 'relative'
        ? 'The page, assets and API share this preview origin. A relative path follows the page’s scheme, host and port; no cross-origin allowance is needed.'
        : target === 'loopback'
          ? 'In browser JavaScript, loopback names the browser’s machine. It does not name the remote learner workspace; browser security rules may also block this mixed-origin request.'
          : 'Opening the HTML file directly does not establish the same HTTP origin as your API. Open the app through its server with the Open app button.',
    warning: target !== 'relative',
  };
}

export const awaitSteps = [
  'Call fetch',
  'Receive response headers',
  'Read and parse JSON body',
] as const;
export type AwaitOutcome = 'success' | 'http-error' | 'network-error';
export function awaitFrame(
  step: number,
  otherClicks: number,
  outcome: AwaitOutcome = 'success',
): ModelFrame {
  const networkFailed = outcome === 'network-error' && step >= 2;
  const httpRejected = outcome === 'http-error' && step >= 3;
  return {
    outputs: [
      output(
        'Async request function',
        networkFailed
          ? 'fetch rejected; enter catch\nNo HTTP response available'
          : httpRejected
            ? 'response.ok is false; helper throws\nEnter catch'
            : step === 0
              ? 'Not started'
              : step === 1
                ? 'await fetch(...)\nwaiting for response'
                : step === 2
                  ? outcome === 'http-error'
                    ? 'fetch fulfilled with status 422\nNext: inspect response.ok'
                    : 'await response.json()\nwaiting for parsed body'
                  : 'tasks = [{id: 7, title: "Call Alex"}]',
      ),
      output(
        'Value available to the next line',
        networkFailed
          ? 'No Response object; request promise rejected'
          : httpRejected
            ? 'HTTP error from the helper, not a task array'
            : step < 1
              ? '(none)'
              : step === 1
                ? 'A pending promise, not a task array'
                : step === 2
                  ? outcome === 'http-error'
                    ? 'Response object: status 422; ok = false'
                    : 'Response object with status and headers'
                  : 'Parsed JavaScript array',
      ),
      output('Other event-handler calls', String(otherClicks)),
    ],
    explanation: networkFailed
      ? 'A connection failure rejects fetch without an HTTP status. It is different from receiving a response whose status rejects the operation.'
      : httpRejected
        ? 'fetch itself fulfilled with a Response. Our helper checks response.ok and deliberately throws; HTTP 422 is not a network rejection.'
        : step === 3
          ? 'This function can now render the parsed array. JSON parsing was asynchronous too.'
          : 'Waiting pauses this async function. Other event handlers can still run. Try the other-action button while either await is waiting.',
    warning: networkFailed || httpRejected,
  };
}

export type ReadOutcome = 'data' | 'empty' | 'failed' | 'unreadable';
export function readFrame(outcome: ReadOutcome, previous: boolean, step: number): ModelFrame {
  const failed = outcome === 'failed' || outcome === 'unreadable';
  const oldRows = previous ? ['Read notes (previous snapshot)'] : [];
  const rows =
    step < 2 || failed ? oldRows : outcome === 'empty' ? [] : ['Read notes', 'Call Alex'];
  const status =
    step === 0
      ? 'Not requested'
      : step === 1
        ? 'Loading…'
        : failed
          ? previous
            ? 'Could not refresh; displayed rows may be stale.'
            : 'Could not load tasks. Try again.'
          : rows.length
            ? 'Loaded 2 tasks.'
            : 'No tasks in this view.';
  return {
    outputs: [
      output('Visible rows', rows.length ? rows.join('\n') : '(no rows drawn)'),
      output(
        'Request result',
        step < 2
          ? '(waiting)'
          : outcome === 'unreadable'
            ? '200 received, but JSON body could not be parsed'
            : outcome === 'failed'
              ? 'Read failed; current server contents unknown'
              : outcome === 'empty'
                ? '200 []'
                : '200 [two task records]',
      ),
      output('Interface message', status),
    ],
    explanation:
      step === 2 && failed
        ? 'A failed read does not prove the list is empty. Preserve previous rows if available, label them as not refreshed, and offer another read.'
        : 'Only a successful empty response supports an empty-state message. Loading, empty and failure describe different knowledge.',
    warning: step === 2 && failed,
  };
}

export type CreationOutcome = 'saved' | 'invalid' | 'unavailable' | 'lost' | 'refresh-failed';
export function creationFrame(outcome: CreationOutcome, step: number, title: string): ModelFrame {
  const saved = step >= 2 && outcome !== 'invalid' && outcome !== 'unavailable';
  const confirmed = saved && outcome !== 'lost';
  const response =
    step < 2
      ? '(not received)'
      : outcome === 'invalid'
        ? '422 · input rejected'
        : outcome === 'unavailable'
          ? '503 · snapshot save failed'
          : outcome === 'lost'
            ? '(response lost after saving)'
            : '201 · created task 4';
  const message =
    step === 0
      ? 'Ready to submit.'
      : step === 1
        ? 'Saving…'
        : outcome === 'invalid'
          ? 'Title was rejected. Keep input and correct it.'
          : outcome === 'unavailable'
            ? 'Could not save. Current data is unchanged.'
            : outcome === 'lost'
              ? 'Could not confirm the save. Reload before repeating a create.'
              : step === 2
                ? 'Saved. Refreshing the current view…'
                : outcome === 'refresh-failed'
                  ? 'Saved, but the list could not refresh. Retry the read.'
                  : 'Saved and displayed in the refreshed list.';
  return {
    outputs: [
      output('Form value', confirmed ? '(cleared after 201)' : title || '(empty)'),
      output(
        'Accepted server state',
        saved ? `Task 4 exists: ${normalizeTitle(title)}` : 'No new task; next_id remains 4',
      ),
      output('Response received', response),
      output('Interface message', message),
    ],
    explanation:
      step === 1
        ? 'The submitted input is frozen while saving. A delayed response cannot erase a newer edit.'
        : outcome === 'lost' && step >= 2
          ? 'The model exposes server state so you can see the uncertainty. The real browser does not know whether this unconfirmed request was saved.'
          : confirmed && step >= 3 && outcome === 'refresh-failed'
            ? 'The create is known to have succeeded. A failed following read must not invite another create.'
            : 'The browser must distinguish what actually happened on the server from what its response proves.',
    warning:
      step >= 2 &&
      (outcome === 'invalid' ||
        outcome === 'unavailable' ||
        outcome === 'lost' ||
        (outcome === 'refresh-failed' && step >= 3)),
  };
}

export type TaskView = 'all' | 'open' | 'completed' | 'reverse';
export function visibleTasks(tasks: readonly ModelTask[], view: TaskView) {
  if (view === 'open') return tasks.filter((task) => !task.completed);
  if (view === 'completed') return tasks.filter((task) => task.completed);
  if (view === 'reverse') return [...tasks].reverse();
  return [...tasks];
}
export function actionFrame(
  view: TaskView,
  identity: 'id' | 'index',
  operation: 'patch' | 'delete',
  performed: boolean,
) {
  const before = visibleTasks(sampleTasks, view);
  const first = before[0];
  const target = identity === 'id' ? first?.id : 1;
  const tasks = sampleTasks.flatMap((task) =>
    performed && task.id === target
      ? operation === 'delete'
        ? []
        : [{ ...task, completed: !(first?.completed ?? false) }]
      : [{ ...task }],
  );
  const request =
    operation === 'delete'
      ? `DELETE /tasks/${String(target)}`
      : `PATCH /tasks/${String(target)}\n${json({ completed: !(first?.completed ?? false) })}`;
  return {
    before,
    tasks,
    after: visibleTasks(tasks, view),
    target,
    wrong: target !== first?.id,
    request,
    response: operation === 'delete' ? '204 · no body to parse' : '200 · accepted updated record',
  };
}

export const orderSteps = [
  'Start reading open tasks (A)',
  'Try switching to completed (B)',
  'Finish B first, or finish guarded A',
  'Finish older A, or start guarded B',
  'Finish guarded B',
] as const;
export function orderingFrame(guarded: boolean, step: number): ModelFrame {
  const selection = guarded ? (step >= 4 ? 'completed' : 'open') : step >= 2 ? 'completed' : 'open';
  const rows = guarded
    ? step >= 5
      ? 'Call Alex (completed)'
      : step >= 3
        ? 'Read notes (open)'
        : '(waiting)'
    : step >= 4
      ? 'Read notes (open) — older A overwrote B'
      : step >= 3
        ? 'Call Alex (completed)'
        : '(waiting)';
  return {
    outputs: [
      output('Selected filter', selection),
      output('Visible result', rows),
      output(
        'Workflow A',
        step < 1 ? 'Not started' : step < (guarded ? 3 : 4) ? 'Read pending' : 'Finished',
      ),
      output(
        'Workflow B',
        step < 2
          ? 'Not attempted'
          : guarded && step < 4
            ? 'Guard refused new workflow; filter unchanged'
            : step < (guarded ? 5 : 3)
              ? 'Read pending'
              : 'Finished',
      ),
    ],
    explanation:
      !guarded && step >= 4
        ? 'The page says completed but shows an older open result. Arrival order is not request order.'
        : guarded
          ? 'One shared guard covers each change and the refresh that follows it. A new action can begin after both finish. Disabling buttons alone does not replace this check.'
          : 'Two reads are allowed to overlap. Predict what happens when the older request finishes last.',
    warning: !guarded && step >= 4,
  };
}
